class MorebornCartSectionRecovery {
  static sequence = 0;
  static controller = null;

  static getSectionKey(section) {
    return section.section || section.id;
  }

  static isValidSectionHTML(html, section) {
    if (typeof html !== 'string' || !html.trim()) return false;

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    return Boolean(parsedHTML.querySelector(section.selector || '.shopify-section'));
  }

  static async ensureSections(parsedState, sectionsToRender, context) {
    const sequence = ++this.sequence;
    if (this.controller) this.controller.abort();
    this.controller = null;

    const missingSections = sectionsToRender.filter((section) => {
      const sectionKey = this.getSectionKey(section);
      return !this.isValidSectionHTML(parsedState?.sections?.[sectionKey], section);
    });

    if (missingSections.length === 0) return parsedState;

    console.warn('[cart-recovery] Missing required cart section HTML; attempting one recovery request.', {
      context,
      missingSections: missingSections.map((section) => this.getSectionKey(section)),
    });

    const controller = new AbortController();
    this.controller = controller;

    try {
      const recoveryUrl = new URL(window.routes?.cart_url || '/cart', window.location.origin);
      recoveryUrl.searchParams.set(
        'sections',
        missingSections.map((section) => this.getSectionKey(section)).join(',')
      );
      recoveryUrl.searchParams.set('sections_url', window.location.pathname);

      const response = await fetch(recoveryUrl.toString(), {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
      });
      const contentType = response.headers.get('content-type') || '';
      if (!response.ok || !contentType.includes('application/json')) throw new Error('Invalid section recovery response');

      const recoveredSections = await response.json();
      if (!recoveredSections || typeof recoveredSections !== 'object' || Array.isArray(recoveredSections)) {
        throw new Error('Invalid section recovery payload');
      }

      if (sequence !== this.sequence) return null;

      for (const section of missingSections) {
        const sectionKey = this.getSectionKey(section);
        if (!this.isValidSectionHTML(recoveredSections[sectionKey], section)) {
          throw new Error('Incomplete section recovery payload');
        }
      }

      return {
        ...parsedState,
        sections: {
          ...(parsedState?.sections || {}),
          ...recoveredSections,
        },
      };
    } catch (error) {
      if (error.name === 'AbortError' || sequence !== this.sequence) return null;
      window.location.assign(window.routes?.cart_url || '/cart');
      return null;
    } finally {
      if (this.controller === controller) this.controller = null;
    }
  }
}

window.MorebornCartSectionRecovery = MorebornCartSectionRecovery;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.protectionObserver = new MutationObserver(() => this.hideProtectionUpsell());
    this.protectionObserver.observe(this, { childList: true, subtree: true });
  }

  hideProtectionUpsell() {
    this.querySelectorAll('button, a, [role="button"]').forEach((element) => {
      const label = (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
      if (label.includes('add accident protection')) {
        element.style.setProperty('display', 'none', 'important');
        element.setAttribute('aria-hidden', 'true');
        element.setAttribute('tabindex', '-1');
      }
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.hideProtectionUpsell();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState) {
    const hydratedState = await window.MorebornCartSectionRecovery.ensureSections(
      parsedState,
      this.getSectionsToRender(),
      'cart-drawer:add'
    );
    if (!hydratedState) return;

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = hydratedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHTML = hydratedState.sections[section.id];

      if (!sectionElement) return;

      if (!sectionHTML) {
        console.warn('[cart-drawer] Missing section HTML:', {
          sectionId: section.id,
        });
        return;
      }

      const sectionInnerHTML = this.getSectionInnerHTML(sectionHTML, section.selector, section.id);

      if (sectionInnerHTML === null) {
        return;
      }

      sectionElement.innerHTML = sectionInnerHTML;
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.hideProtectionUpsell();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section', sectionId = 'unknown') {
    if (!html) {
      console.warn('[cart-drawer] Missing section HTML:', {
        sectionId,
      });
      return null;
    }

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const target = parsedHTML.querySelector(selector);

    if (!target) {
      console.warn('[cart-drawer] Missing selector in section HTML:', {
        sectionId,
        selector,
      });
      return null;
    }

    return target.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionInnerHTML(html, selector = '.shopify-section', sectionId = 'unknown') {
    if (!html) {
      console.warn('[cart-drawer] Missing section HTML:', {
        sectionId,
      });
      return null;
    }

    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const target = parsedHTML.querySelector(selector);

    if (!target) {
      console.warn('[cart-drawer] Missing selector in section HTML:', {
        sectionId,
        selector,
      });
      return null;
    }

    return target.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
